import { motion } from 'motion/react';
import { staggerEntrance } from './motionUi';

const coverGradients = [
  'from-game-popup via-game-purpleMid to-game-banner',
  'from-game-menuTop via-game-popup to-game-purpleDeep',
  'from-game-purpleMid to-game-banner',
  'from-game-popup to-game-menuTop'
];

/**
 * Сетка альбомов персонажа (обложка + название + счётчик) — как {@link GameCharacterAlbumsModal} в UI kit.
 * @param {object} props
 * @param {Array<{ id: string, title: string, photoCount: number, coverImageUrl?: string, owned: boolean, price?: number }>} props.albums
 * @param {(setId: string) => void} [props.onOpenAlbum] — открыть разблокированный альбом (просмотр сета)
 * @param {(album: object) => void} [props.onUnlockAlbum] — купить / разблокировать (если не передан — кнопка заблокирована)
 */
export default function GameCharacterAlbumsPanel({ albums, onOpenAlbum, onUnlockAlbum }) {
  if (!albums?.length) {
    return (
      <div className="p-8 text-center text-sm text-game-muted">No albums for this character yet.</div>
    );
  }

  return (
    <div className="p-3 sm:p-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        {albums.map((a, i) => (
          <motion.div
            key={a.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={staggerEntrance(i, 0.05, 0)}
            className="flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-game-surface text-left shadow-lg"
          >
            <div className="relative aspect-square w-full shrink-0 overflow-hidden">
              {a.coverImageUrl ? (
                <img
                  src={a.coverImageUrl}
                  alt=""
                  className="h-full w-full object-cover"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : (
                <div
                  className={`h-full w-full bg-gradient-to-br ${coverGradients[i % coverGradients.length]}`}
                />
              )}
              {a.owned ? (
                <span className="pointer-events-none absolute left-2 top-2 rounded-md bg-black/55 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                  Library
                </span>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/50 px-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-white/80">Locked</span>
                  {typeof a.price === 'number' ? (
                    <span className="text-sm font-bold tabular-nums text-game-accent">{a.price} ◆</span>
                  ) : null}
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col p-2.5 sm:p-3">
              <p className="line-clamp-2 text-xs font-semibold leading-snug text-game-text sm:text-[13px]">{a.title}</p>
              <p className="mt-1 text-[10px] text-game-muted sm:text-[11px]">{a.photoCount} photos</p>
              {a.owned ? (
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.99 }}
                  onClick={() => onOpenAlbum?.(a.id)}
                  className="mt-3 w-full rounded-xl border border-white/15 bg-transparent py-2.5 text-center text-[11px] font-bold text-game-text sm:text-xs"
                >
                  Open
                </motion.button>
              ) : (
                <motion.button
                  type="button"
                  whileTap={{ scale: onUnlockAlbum ? 0.99 : 1 }}
                  disabled={!onUnlockAlbum}
                  onClick={() => onUnlockAlbum?.(a)}
                  className={`mt-3 w-full rounded-xl py-2.5 text-center text-[11px] font-bold sm:text-xs ${
                    onUnlockAlbum
                      ? 'bg-game-accent text-game-accentText'
                      : 'cursor-not-allowed bg-game-muted/25 text-game-muted'
                  }`}
                >
                  Unlock
                </motion.button>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
